// src/utils/creditsFormat.js
//
// Display helpers for the Credits dashboard. Every credit/cost field arrives as
// a lossless Decimal STRING on the wire — parse before formatting/arithmetic.
// Display precision (integer credits, 2-decimal USD) is fine for the dashboard;
// we never write these values back, so JS-number rounding is acceptable here.

const creditsFmt = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });
const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Parse a Decimal-string field to a finite Number, or null for null/blank/NaN. */
export function parseDecimal(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** it-IT thousands-separated integer credits (e.g. "250.000"). null/NaN -> "—". */
export function formatCredits(value) {
  const n = parseDecimal(value);
  if (n == null) return '—';
  return creditsFmt.format(Math.round(n));
}

/** USD currency string (e.g. "$1,830.00"). null/NaN -> null (caller hides UI). */
export function formatMoney(value) {
  const n = parseDecimal(value);
  if (n == null) return null;
  return moneyFmt.format(n);
}

/**
 * Consumed fraction (0..1) of the monthly allowance, for the gauge bar.
 * Returns null when there is no plan or a non-positive allowance (untracked).
 */
export function consumedFraction(consumed, monthly) {
  const c = parseDecimal(consumed);
  const m = parseDecimal(monthly);
  if (c == null || m == null || m <= 0) return null;
  return Math.min(1, Math.max(0, c / m));
}

/**
 * Europe/Rome calendar-month label (e.g. "Giugno 2026") from period_start.
 * period_start is the UTC instant of the Rome month boundary, so we format the
 * instant in the Rome timezone to recover the intended month.
 */
export function formatPeriodLabel(periodStartIso) {
  if (!periodStartIso) return '';
  const d = new Date(periodStartIso);
  if (Number.isNaN(d.getTime())) return '';
  const label = d.toLocaleDateString('it-IT', {
    month: 'long', year: 'numeric', timeZone: 'Europe/Rome',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}
